import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import { ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { operationalDashboardService } from '../../services/operationalDashboard';
import type { PaleteItem, PaginationInfo, DashboardFilters } from '../../services/operationalDashboard';

interface Props {
  filters: DashboardFilters;
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    pendente: 'Pendente',
    pre_carregamento: 'Pré-Carregamento',
    carregado: 'Carregado',
    descarregado: 'Descarregado',
    cancelado: 'Cancelado'
  };
  return map[status] || status;
}

function statusColor(status: string): string {
  const map: Record<string, string> = {
    pendente: 'yellow',
    pre_carregamento: 'blue',
    carregado: 'green',
    descarregado: 'cyan',
    cancelado: 'gray'
  };
  return map[status] || 'gray';
}

type SortField = 'numero_palete' | 'status' | 'conferente_nome' | 'cliente_nome' | 'total_etiquetas' | 'total_pedidos' | 'total_duplicadas' | 'peso_total';
type SortDir = 'asc' | 'desc';

const OperationalDashboardCargas: React.FC<Props> = ({ filters }) => {
  const [paletes, setPaletes] = useState<PaleteItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagina, setPagina] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFilters = useMemo(() => filters, [filters.periodo, filters.dataInicio, filters.dataFim]);

  useEffect(() => {
    setPagina(1);
  }, [stableFilters]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await operationalDashboardService.getCargas({ ...stableFilters, pagina, limite: 50 });
        setPaletes(res.data || []);
        setPagination(res.pagination || null);
      } catch (err: unknown) {
        setError('Erro ao carregar paletes. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [stableFilters, pagina]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedPaletes = [...paletes].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    const aStr = String(aVal || '');
    const bStr = String(bVal || '');
    return sortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} style={{ opacity: 0.4, marginLeft: 4 }} />;
    return sortDir === 'asc'
      ? <ArrowUp size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />
      : <ArrowDown size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />;
  };

  if (loading) {
    return <div className="op-dashboard-loading"><div className="op-dashboard-spinner" />Carregando paletes...</div>;
  }

  if (error) {
    return <div className="op-dashboard-error">{error}</div>;
  }

  return (
    <>
      <div className="op-dashboard-realtime-header">
        <h2 className="op-dashboard-section-title">Paletes</h2>
        {pagination && (
          <span style={{ color: '#94a3b8', fontSize: 13 }}>
            {pagination.total} palete(s) encontrado(s)
          </span>
        )}
      </div>

      {sortedPaletes.length > 0 ? (
        <>
          <div className="op-dashboard-card-section" style={{ overflowX: 'auto' }}>
            <table className="op-dashboard-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('numero_palete')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Palete <SortIcon field="numero_palete" />
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Status <SortIcon field="status" />
                  </th>
                  <th onClick={() => handleSort('cliente_nome')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Cliente <SortIcon field="cliente_nome" />
                  </th>
                  <th>Região</th>
                  <th>Área de Entrega</th>
                  <th onClick={() => handleSort('conferente_nome')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Conferente <SortIcon field="conferente_nome" />
                  </th>
                  <th onClick={() => handleSort('total_pedidos')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Pedidos <SortIcon field="total_pedidos" />
                  </th>
                  <th onClick={() => handleSort('total_etiquetas')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Etiquetas <SortIcon field="total_etiquetas" />
                  </th>
                  <th onClick={() => handleSort('total_duplicadas')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Duplicadas <SortIcon field="total_duplicadas" />
                  </th>
                  <th onClick={() => handleSort('peso_total')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Peso (kg) <SortIcon field="peso_total" />
                  </th>
                  <th>Doca</th>
                  <th>Op.</th>
                </tr>
              </thead>
              <tbody>
                {sortedPaletes.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>
                      #{p.numero_palete}
                    </td>
                    <td>
                      <span className={`op-dashboard-badge ${statusColor(p.status)}`}>
                        {statusLabel(p.status)}
                      </span>
                    </td>
                    <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={p.cliente_nome}>
                      {p.cliente_nome}
                    </td>
                    <td>{p.area_regiao || '-'}</td>
                    <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={p.area_codigo && p.area_nome ? `${p.area_codigo} (${p.area_nome})` : (p.area_codigo || p.area_nome || '-')}>
                      {p.area_codigo && p.area_nome ? `${p.area_codigo} (${p.area_nome})` : (p.area_codigo || p.area_nome || '-')}
                    </td>
                    <td>{p.conferente_nome}</td>
                    <td>{p.total_pedidos}</td>
                    <td>{p.total_etiquetas.toLocaleString('pt-BR')}</td>
                    <td>
                      <span className={`op-dashboard-badge ${p.total_duplicadas > 0 ? 'red' : 'green'}`}>
                        {p.total_duplicadas}
                      </span>
                    </td>
                    <td>{p.peso_total > 0 ? p.peso_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                    <td>{p.doca || '-'}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>#{p.numero_operacao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {pagination && pagination.totalPaginas > 1 && (
            <div className="op-dashboard-pagination">
              <button
                className="op-dashboard-filter-btn"
                disabled={pagina <= 1}
                onClick={() => setPagina(p => Math.max(1, p - 1))}
              >
                <ChevronLeft size={16} /> Anterior
              </button>
              <span style={{ color: '#e2e8f0', fontSize: 14 }}>
                Página {pagination.pagina} de {pagination.totalPaginas}
              </span>
              <button
                className="op-dashboard-filter-btn"
                disabled={pagina >= pagination.totalPaginas}
                onClick={() => setPagina(p => p + 1)}
              >
                Próxima <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="op-dashboard-empty">Sem paletes no período selecionado</div>
      )}
    </>
  );
};

export default OperationalDashboardCargas;
